import { Badge, Breadcrumb, Button, Dropdown, Segmented, Select, Tooltip } from 'antd';
import {
  type BreadcrumbItemType,
  type BreadcrumbSeparatorType,
} from 'antd/lib/breadcrumb/Breadcrumb';
import clsx from 'clsx';
import React, { type Key, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileCategoryEnum } from '../../enums/FileCategoryEnum';
import { FileMenuActionEnum } from '../../enums/FileMenuActionEnum';
import { FilesTableEnum } from '../../enums/FilesTableEnum';
import { useAuth } from '../../hooks/useAuth';
import { useRightPanel } from '../../hooks/useRightPanel';
import type File from '../../interfaces/File';
import type FileAction from '../../interfaces/FileAction';
import type { GetFilesPayload } from '../../interfaces/FileService';
import UserMultiSelect from '../Common/UserMultiSelect';
import { Icon } from '../Core/Icon';
import FileDetail from './FileDetail';
import FilesActions from './FilesActions';

type FilterConfig = {
  key: string;
  group: 'left' | 'right';
  show: boolean;
  render: (className: string) => React.ReactNode;
};

interface FilesHeaderProps {
  isLoading: boolean;
  selectedFiles: File[];
  selectedTableKeys: Key[];
  setSelectedTableKeys: (keys: number[]) => void;
  isFilesListInGridMode: boolean;
  setIsFilesListInGridMode: (mode: boolean) => void;
  onFiltersChange: (filters: GetFilesPayload) => void;
  filters: GetFilesPayload;
  handleFileMenuAction: (filesIds: number[], action: FileMenuActionEnum) => void;
  breadcrumbItems: Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[];
  section?: FilesTableEnum;
  filesActions: FileAction[];
}

const FilesHeader: React.FC<FilesHeaderProps> = ({
  selectedFiles,
  selectedTableKeys,
  setSelectedTableKeys,
  isFilesListInGridMode,
  setIsFilesListInGridMode,
  onFiltersChange,
  filters,
  handleFileMenuAction,
  breadcrumbItems,
  section,
  filesActions,
}) => {
  const { t } = useTranslation();
  const { isInFileRoute } = useAuth(); // isAdminOrContributor, isReader
  const { isRightPanelOpen, openRightPanel, closeRightPanel, setRightPanelContent } =
    useRightPanel();

  /**
   * Toggles the right panel open or closed.
   */
  const handleToggleRightPanel = () => {
    if (isRightPanelOpen) {
      closeRightPanel();
    } else {
      openRightPanel();
    }
  };

  const key = section === FilesTableEnum.Received ? 'sharedFrom' : 'shareWith';

  const filterConfigs: FilterConfig[] = [
    {
      key: 'fileTypes',
      group: 'left',
      show: true,
      render: (className) => (
        <Select
          aria-label="File types"
          className={className}
          allowClear
          mode="multiple"
          maxTagCount={1}
          showSearch={false}
          value={filters.fileTypes}
          placeholder={t('common.fileTypes')}
          onChange={(value) => {
            onFiltersChange({
              ...filters,
              fileTypes: value,
              offset: 0,
            });
          }}
          options={[
            {
              label: t('common.documents'),
              value: FileCategoryEnum.Documents,
            },
            {
              label: t('common.images'),
              value: FileCategoryEnum.Images,
            },
            {
              label: t('common.audio'),
              value: FileCategoryEnum.Audio,
            },
            {
              label: t('common.video'),
              value: FileCategoryEnum.Video,
            },
            {
              label: t('common.other'),
              value: FileCategoryEnum.Other,
            },
          ]}
        />
      ),
    },
    {
      key: 'shareWith',
      group: 'left',
      show: true, // isAdminOrContributor || isReader,
      render: (className) => (
        <UserMultiSelect
          className={className}
          placeholder={`${t(section === FilesTableEnum.Received ? 'common.showFilesSharedBy' : 'common.showFilesSharedWith')}...`}
          includeGroups={section !== FilesTableEnum.Received}
          selectedUserIds={filters?.[key]
            ?.filter((user: { id: number; type: number }) => user.type === 0)
            .map((user: { id: number; type: number }) => user.id)}
          selectedGroupIds={filters?.shareWith
            ?.filter((user) => user.type === 1)
            .map((user) => user.id)}
          onUserChange={(ids) => {
            const users = ids.map((id) => ({ id, type: 0 }));

            onFiltersChange({
              ...filters,
              [key]: [...(filters?.[key] || []).filter((i) => i.type === 1), ...users],
              offset: 0,
            });
          }}
          additionalUserFilters={{
            shareFilter: section === FilesTableEnum.Received ? 3 : 1,
          }}
          additionalGroupFilters={{
            shareFilter: 1,
          }}
          onGroupChange={(ids) => {
            const groups = ids.map((id) => ({ id, type: 1 }));
            onFiltersChange({
              ...filters,
              shareWith: [...(filters?.shareWith || []).filter((i) => i.type === 0), ...groups],
              offset: 0,
            });
          }}
          onReset={() => {
            onFiltersChange({
              ...filters,
              shareWith: [],
              sharedFrom: [],
              offset: 0,
            });
          }}
        />
      ),
    },
    // {
    //   key: 'onBehalfOf',
    //   group: 'right',
    //   show: isAdmin,
    //   render: (className) => (
    //     <UserSelect
    //       className={className}
    //       aria-label={t('common.onBehalfOf')}
    //       placeholder={t('common.onBehalfOf')}
    //       isDisabled={isLoading || !isAdmin}
    //       allowClear
    //       selectedUser={onBehalfOfUser}
    //       excludeUserIds={authenticatedUser ? [authenticatedUser?.id] : []}
    //       onChange={(_, user) => {
    //         if (user?.id !== onBehalfOfUser?.id) {
    //           handleOnBehalfOfUserChange(user);
    //         }
    //       }}
    //     />
    //   ),
    // },
  ];

  const [isDetailsButtonAnimated, setIsDetailsButtonAnimated] = useState(false);

  useEffect(() => {
    if (isRightPanelOpen) {
      setIsDetailsButtonAnimated(false);
      return;
    }

    if (selectedFiles.length === 0) {
      setIsDetailsButtonAnimated(false);
    } else if (selectedFiles.length === 1) {
      setIsDetailsButtonAnimated(true);
    } else {
      setIsDetailsButtonAnimated(false);
    }

    const handle = window.setTimeout(() => {
      setIsDetailsButtonAnimated(false);
    }, 1000);
    return () => window.clearTimeout(handle);
  }, [selectedFiles]);

  useEffect(() => {
    if (isRightPanelOpen) {
      if (selectedFiles.length === 1) {
        if (selectedFiles?.[0]) {
          setRightPanelContent(<FileDetail file={selectedFiles[0]} />);
        }
      } else if (selectedFiles.length > 1) {
        setRightPanelContent(
          <div className="px-4 text-xl font-medium">
            {t('common.selectedItems')}: {selectedFiles.length}
          </div>,
        );
      } else {
        setRightPanelContent(
          <div className="px-4 text-xl font-medium">{t('common.selectItemToViewDetails')}</div>,
        );
      }
    }
  }, [isRightPanelOpen, selectedFiles]);

  const activeFiltersCount = useMemo(() => {
    return (
      (filters?.fileTypes?.length || 0) +
      (filters?.shareWith?.length || 0) +
      (filters?.onBehalfOf !== undefined ? 1 : 0)
    );
  }, [filters]);

  return (
    <div className="relative flex w-full flex-col">
      <div
        className={clsx(
          'flex w-full flex-row items-center justify-between gap-2 rounded-md transition-all duration-200 ease-in-out',
          selectedTableKeys.length > 0 && 'bg-white py-1 shadow-md',
        )}
      >
        {selectedTableKeys.length > 0 ? (
          <FilesActions
            handleFileMenuAction={handleFileMenuAction}
            files={selectedFiles}
            filesActions={filesActions}
          />
        ) : (
          <>
            <div className="hidden w-full flex-row items-center justify-between gap-2 lg:flex">
              <div className="grid w-full grid-cols-2 gap-2 xl:grid-cols-3">
                {filterConfigs
                  .filter((f) => f.group === 'left' && f.show)
                  .map((f) => (
                    <div key={f.key + '-left'}>{f.render('w-full')}</div>
                  ))}
              </div>

              {filterConfigs
                .filter((f) => f.group === 'right' && f.show)
                .map((f) => (
                  <div key={f.key + '-left'}>{f.render('w-72')}</div>
                ))}
            </div>
            <div className="z-10 flex items-center justify-start lg:hidden">
              <Badge size="small" count={activeFiltersCount} offset={[0, 7]}>
                <Dropdown
                  popupRender={() => (
                    <div className="grid w-[300px] grid-cols-1 gap-3 rounded-md border border-gray-medium bg-white p-2 shadow-md">
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-medium">{t('common.filters')}</span>
                        {/* <Button
                          type="text"
                          onClick={() => {
                            onFiltersChange({
                              ...filters,
                              fileTypes: [],
                              shareWith: [],
                              onBehalfOf: undefined,
                              offset: 0,
                            });
                          }}
                        >
                          <Icon name="XIcon" className="w-5" />
                        </Button> */}
                      </div>
                      {filterConfigs.filter((f) => f.show).map((f) => f.render('w-full'))}
                    </div>
                  )}
                >
                  <Button className="flex items-center">
                    <Icon name="ListFilterIcon" className="w-5" />
                  </Button>
                </Dropdown>
              </Badge>
            </div>
          </>
        )}
        <div
          className={clsx(
            'flex flex-shrink-0 flex-row items-center gap-2 transition-all duration-200',
          )}
        >
          {selectedTableKeys.length > 0 && (
            <div className="border-r border-gray-medium pr-2">
              <Button
                onClick={() => {
                  setSelectedTableKeys([]);
                }}
              >
                <span>
                  {t('common.selectedItems')}: {selectedFiles.length}
                </span>
                <Icon name="XIcon" className="w-5" />
              </Button>
            </div>
          )}
          <Segmented
            value={isFilesListInGridMode}
            onChange={(value) => {
              setSelectedTableKeys([]);
              setIsFilesListInGridMode(value);
            }}
            options={[
              {
                value: false,
                icon: (
                  <Tooltip title="View as list">
                    <div>
                      <Icon name="LayoutListIcon" className="mt-0.5 w-5" />
                    </div>
                  </Tooltip>
                ),
              },

              {
                value: true,
                icon: (
                  <Tooltip title="View as grid">
                    <div>
                      <Icon name="LayoutGridIcon" className="mt-0.5 w-5" />
                    </div>
                  </Tooltip>
                ),
              },
            ]}
          />
          <Button
            className={clsx(
              'transition-all duration-300',
              isDetailsButtonAnimated && 'bg-azure/50',
            )}
            onClick={handleToggleRightPanel}
          >
            <Icon
              name={isRightPanelOpen ? 'PanelRightCloseIcon' : 'PanelRightOpenIcon'}
              className="w-5 transition-all duration-300"
            />
            <span>{t('common.details')}</span>
          </Button>
        </div>
      </div>
      <div className="absolute -bottom-5 left-1 w-full">
        <div className="flex flex-row items-center justify-between">
          <Breadcrumb items={breadcrumbItems} />
          {isInFileRoute(FilesTableEnum.Trash) && (
            <div className="flex flex-shrink-0 flex-row items-center px-2">
              <Button
                onClick={() => {
                  handleFileMenuAction([], FileMenuActionEnum.EmptyTrash);
                }}
                size="small"
                type="text"
                danger
              >
                <Icon name="TrashIcon" className="w-5" />
                {t('common.emptyTrash')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilesHeader;
